import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import Cropper from 'react-easy-crop';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Image as ImageIcon, Send, Clock, Loader2, Check, Crop as CropIcon, X, AlertCircle,
  Download, Save, Sparkles, Hash, Link2, FolderOpen, AlertTriangle, PenLine,
} from 'lucide-react';
import { toastPublishOutcome } from '@/lib/socialPublishOutcome';
import EmojiPicker from '@/components/social/EmojiPicker';
import UtmBuilder from '@/components/social/UtmBuilder';
import HashtagAssistant from '@/components/social/HashtagAssistant';
import AiCopyStudio from '@/components/social/AiCopyStudio';
import MediaLibraryPicker from '@/components/social/MediaLibraryPicker';

const POST_TYPE_LABELS = { feed: 'Publicación', reel: 'Reel (video vertical)', story: 'Historia (vertical)' };
const SUPPORTED_POST_TYPES = { facebook: ['feed'], instagram: ['feed', 'reel', 'story'] };
const NETWORK_LABELS = { facebook: 'Facebook', instagram: 'Instagram' };
const OBJECTIVES = [
  ['awareness', 'Reconocimiento'], ['engagement', 'Interacción'], ['leads', 'Captar lead'],
  ['sales', 'Venta'], ['traffic', 'Tráfico'], ['education', 'Educación'],
];
const AUTOSAVE_DEBOUNCE_MS = 1500;

async function getCroppedBlob(imageSrc, cropPixels, mimeType) {
  const image = await new Promise((resolve, reject) => {
    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = imageSrc;
  });
  const canvas = document.createElement('canvas');
  canvas.width = cropPixels.width;
  canvas.height = cropPixels.height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(image, cropPixels.x, cropPixels.y, cropPixels.width, cropPixels.height, 0, 0, cropPixels.width, cropPixels.height);
  return new Promise((resolve) => canvas.toBlob(resolve, mimeType || 'image/jpeg', 0.92));
}

function toLocalInputValue(date) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function emptyVariant() {
  return { use_override: false, content_text: '', media_key: null, media_url: null, post_type: null, cta: '', cta_url: '', hashtags: [] };
}

/**
 * Fase 2 — Compositor profesional. Dos columnas en escritorio (contenido
 * / vista previa), tabs en móvil. Borrador con autoguardado y
 * concurrencia optimista (draft_version). Contenido global + override por
 * red (tabla SocialPostNetworkVariant, nunca se duplica el post entero).
 */
export default function SocialPostComposer({ open, onClose, onCreated, initialDateTime = null, post = null }) {
  const [postId, setPostId] = useState(null);
  const [draftVersion, setDraftVersion] = useState(1);
  const [status, setStatus] = useState('draft');
  const [networks, setNetworks] = useState([]);
  const [selectedNetworks, setSelectedNetworks] = useState([]);
  const [activeNetwork, setActiveNetwork] = useState('facebook');
  const [contentText, setContentText] = useState('');
  const [postType, setPostType] = useState('feed');
  const [scheduledAt, setScheduledAt] = useState('');
  const [mediaKey, setMediaKey] = useState(null);
  const [mediaUrl, setMediaUrl] = useState(null);
  const [thumbnailKey, setThumbnailKey] = useState(null);
  const [isVideo, setIsVideo] = useState(false);
  const [internalNotes, setInternalNotes] = useState('');
  const [objective, setObjective] = useState('');
  const [audience, setAudience] = useState('');
  const [cta, setCta] = useState('');
  const [ctaUrl, setCtaUrl] = useState('');
  const [hashtags, setHashtags] = useState([]);
  const [variants, setVariants] = useState({});
  const [mobileTab, setMobileTab] = useState('content');
  const [contentTab, setContentTab] = useState('editar');

  const [localPreviewUrl, setLocalPreviewUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showCropper, setShowCropper] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [utmOpen, setUtmOpen] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [autosaveState, setAutosaveState] = useState('idle'); // idle | saving | saved | error
  const [loaded, setLoaded] = useState(false);

  const fileInputRef = useRef(null);
  const contentRef = useRef(null);
  const autosaveTimer = useRef(null);
  const skipNextAutosave = useRef(true);

  const editable = status === 'draft' || status === 'scheduled' || status === 'failed';

  useEffect(() => {
    if (!open) return;
    setLoaded(false);
    skipNextAutosave.current = true;
    api.get('/tenant/social-posts/networks').then((r) => setNetworks(r.data.networks || [])).catch(() => {});

    const init = async () => {
      if (post) {
        try {
          const { data } = await api.get(`/tenant/social-posts/${post.id}`);
          hydrate(data);
        } catch (e) {
          toast.error(formatApiError(e.response?.data?.detail));
        }
      } else {
        try {
          const { data } = await api.post('/tenant/social-posts/draft', { target_networks: [] });
          hydrate(data);
          setScheduledAt(initialDateTime ? toLocalInputValue(initialDateTime) : '');
        } catch (e) {
          toast.error(formatApiError(e.response?.data?.detail));
        }
      }
      setLoaded(true);
      setTimeout(() => { skipNextAutosave.current = false; }, 300);
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function hydrate(data) {
    setPostId(data.id);
    setDraftVersion(data.draft_version || 1);
    setStatus(data.status);
    setContentText(data.content_text || '');
    setPostType(data.post_type || 'feed');
    setSelectedNetworks(data.target_networks || []);
    setActiveNetwork((data.target_networks || ['facebook'])[0] || 'facebook');
    setScheduledAt(data.scheduled_at ? toLocalInputValue(new Date(data.scheduled_at)) : '');
    setMediaKey(data.media_key || null);
    setMediaUrl(data.media_url || null);
    setThumbnailKey(null);
    setIsVideo(!!data.media_is_video);
    setLocalPreviewUrl(data.media_url || null);
    setInternalNotes(data.internal_notes || '');
    setObjective(data.objective || '');
    setAudience(data.audience || '');
    setCta(data.cta || '');
    setCtaUrl(data.cta_url || '');
    setHashtags(data.hashtags || []);
    const v = {};
    for (const [net, vv] of Object.entries(data.variants || {})) {
      v[net] = { use_override: vv.use_override, content_text: vv.content_text || '', media_key: vv.media_key || null,
                 media_url: vv.media_url || null, post_type: vv.post_type || null, cta: vv.cta || '',
                 cta_url: vv.cta_url || '', hashtags: vv.hashtags || [] };
    }
    setVariants(v);
  }

  // ── Autosave ─────────────────────────────────────────────────────────
  const buildBody = useCallback(() => ({
    draft_version: draftVersion, content_text: contentText, media_key: mediaKey, media_url: mediaUrl,
    thumbnail_key: thumbnailKey, post_type: postType, target_networks: selectedNetworks,
    scheduled_at: scheduledAt ? new Date(scheduledAt).toISOString() : null,
    internal_notes: internalNotes, objective: objective || null, audience: audience || null,
    cta: cta || null, cta_url: ctaUrl || null, hashtags, methodology_key: null,
    variants: Object.fromEntries(Object.entries(variants).map(([net, v]) => [net, v])),
  }), [draftVersion, contentText, mediaKey, mediaUrl, thumbnailKey, postType, selectedNetworks, scheduledAt,
      internalNotes, objective, audience, cta, ctaUrl, hashtags, variants]);

  useEffect(() => {
    if (!open || !loaded || !postId || status !== 'draft') return;
    if (skipNextAutosave.current) return;
    clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(async () => {
      setAutosaveState('saving');
      try {
        const { data } = await api.put(`/tenant/social-posts/draft/${postId}`, buildBody());
        setDraftVersion(data.draft_version);
        setAutosaveState('saved');
      } catch (e) {
        if (e.response?.status === 409) {
          toast.warning('Este borrador se editó en otra pestaña — se recargó la versión más reciente.');
          hydrate(e.response.data?.detail?.current || e.response.data?.current);
        }
        setAutosaveState('error');
      }
    }, AUTOSAVE_DEBOUNCE_MS);
    return () => clearTimeout(autosaveTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentText, mediaKey, thumbnailKey, postType, selectedNetworks, scheduledAt, internalNotes,
      objective, audience, cta, ctaUrl, hashtags, variants]);

  // ── Network selection / variants ────────────────────────────────────
  const toggleNetwork = (key) => {
    setSelectedNetworks((s) => (s.includes(key) ? s.filter((k) => k !== key) : [...s, key]));
  };

  const getVariant = (net) => variants[net] || emptyVariant();
  const setVariantField = (net, field, value) => {
    setVariants((v) => ({ ...v, [net]: { ...getVariant(net), [field]: value } }));
  };

  const getEffective = (net) => {
    const v = getVariant(net);
    if (v.use_override) {
      return { content_text: v.content_text, media_url: v.media_url || (v.media_key ? mediaUrl : null), is_video: isVideo, post_type: v.post_type || postType, hashtags: v.hashtags?.length ? v.hashtags : hashtags };
    }
    return { content_text: contentText, media_url: mediaUrl, is_video: isVideo, post_type: postType, hashtags };
  };

  // ── Media upload ─────────────────────────────────────────────────────
  // Sprint 66 — cuando el toggle "Editar por red" está activo para la red
  // en pantalla, la imagen/video sube a la VARIANTE de esa red (nunca al
  // contenido global) — así se cumple el override real de media por red,
  // no solo de texto.
  const overrideActive = getVariant(activeNetwork).use_override;

  const uploadFile = useCallback(async (file) => {
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('networks', selectedNetworks.join(','));
      form.append('post_type', postType);
      const { data } = await api.post('/tenant/social-posts/media', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (overrideActive) {
        setVariantField(activeNetwork, 'media_key', data.media_key);
        setVariantField(activeNetwork, 'media_url', null);
      } else {
        setMediaKey(data.media_key);
        setMediaUrl(data.media_url);
        setThumbnailKey(data.thumbnail_key);
      }
      setIsVideo(data.is_video);
      const failing = Object.entries(data.format_check || {}).filter(([, v]) => !v.ok);
      if (failing.length > 0 && !data.is_video) { setShowCropper(true); } else { setShowCropper(false); }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setUploading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNetworks, postType, overrideActive, activeNetwork]);

  const onFileSelected = (file) => {
    if (!file) return;
    setIsVideo(file.type.startsWith('video/'));
    const url = URL.createObjectURL(file);
    setLocalPreviewUrl(url);
    uploadFile(file);
  };

  const applyCrop = async () => {
    if (!croppedAreaPixels || !localPreviewUrl) return;
    setUploading(true);
    try {
      const blob = await getCroppedBlob(localPreviewUrl, croppedAreaPixels, 'image/jpeg');
      const croppedFile = new File([blob], 'recorte.jpg', { type: 'image/jpeg' });
      setLocalPreviewUrl(URL.createObjectURL(blob));
      await uploadFile(croppedFile);
      setShowCropper(false);
    } catch {
      toast.error('No se pudo recortar la imagen');
    } finally {
      setUploading(false);
    }
  };

  const removeMedia = () => {
    if (overrideActive) {
      setVariantField(activeNetwork, 'media_key', null);
      setVariantField(activeNetwork, 'media_url', null);
    } else {
      setMediaKey(null); setMediaUrl(null); setThumbnailKey(null);
    }
    setLocalPreviewUrl(null); setShowCropper(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const pickFromLibrary = (asset) => {
    setIsVideo((asset.file_mime_type || '').startsWith('video/'));
    setLocalPreviewUrl(asset.public_url);
    if (overrideActive) {
      setVariantField(activeNetwork, 'media_key', null);
      setVariantField(activeNetwork, 'media_url', asset.public_url);
    } else {
      setMediaKey(null);
      setMediaUrl(asset.public_url);
      setThumbnailKey(null);
    }
  };

  const insertAtCursor = (text) => {
    const el = contentRef.current;
    if (!el) { setContentText((t) => t + text); return; }
    const start = el.selectionStart ?? contentText.length;
    const end = el.selectionEnd ?? contentText.length;
    const next = contentText.slice(0, start) + text + contentText.slice(end);
    setContentText(next);
    requestAnimationFrame(() => { el.focus(); el.selectionStart = el.selectionEnd = start + text.length; });
  };

  const applyAiCopy = (variant, target) => {
    const text = `${variant.hook}\n\n${variant.body}\n\n${variant.cta}`;
    if (target === 'global') {
      setContentText(text);
      setHashtags((h) => Array.from(new Set([...h, ...(variant.hashtags || [])])));
    } else {
      setVariantField(target, 'use_override', true);
      setVariantField(target, 'content_text', text);
      setVariantField(target, 'hashtags', variant.hashtags || []);
    }
    toast.success('Copy aplicado');
  };

  // ── Validation ───────────────────────────────────────────────────────
  const validation = useMemo(() => {
    const errors = []; const warnings = [];
    if (selectedNetworks.length === 0) errors.push('Elige al menos una red.');
    for (const net of selectedNetworks) {
      const info = networks.find((n) => n.key === net);
      const eff = getEffective(net);
      const label = NETWORK_LABELS[net] || net;
      if (info && info.status === 'active' && !info.connected) errors.push(`${label}: no está conectada.`);
      if (net === 'facebook' && info?.can_publish === false) warnings.push(`${label}: sin permiso de publicación todavía — reautoriza Meta.`);
      if (!SUPPORTED_POST_TYPES[net]?.includes(eff.post_type)) errors.push(`${label}: el formato "${POST_TYPE_LABELS[eff.post_type] || eff.post_type}" no está disponible.`);
      if (net === 'instagram' && !eff.media_url) errors.push(`${label}: requiere una imagen o video.`);
      if (net === 'instagram' && eff.post_type === 'reel' && !eff.is_video) errors.push(`${label}: Reel requiere video.`);
      if (net === 'instagram' && eff.post_type === 'feed' && eff.is_video) errors.push(`${label}: el feed clásico no admite video — usa Reel.`);
      if (!eff.content_text?.trim() && !eff.media_url) errors.push(`${label}: escribe un texto o agrega una imagen.`);
    }
    if (ctaUrl) { try { new URL(ctaUrl); } catch { warnings.push('La URL del CTA no parece válida.'); } }
    if (scheduledAt && new Date(scheduledAt) < new Date()) warnings.push('La fecha programada está en el pasado — se publicará de inmediato.');
    return { errors, warnings };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNetworks, networks, contentText, mediaUrl, isVideo, postType, variants, ctaUrl, scheduledAt]);

  // ── Submit ───────────────────────────────────────────────────────────
  const submit = async () => {
    if (validation.errors.length > 0) { toast.error(validation.errors[0]); return; }
    setSubmitting(true);
    try {
      const body = buildBody();
      delete body.draft_version;
      const { data: saved } = await api.put(`/tenant/social-posts/${postId}`, body);
      toastPublishOutcome(toast, saved);
      onCreated?.();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const activeVariant = getVariant(activeNetwork);
  const activeEffective = getEffective(activeNetwork);

  // Editor de media siempre muestra el archivo del TARGET activo (global
  // o variante), para que "Quitar"/recorte actúen sobre lo que se ve.
  useEffect(() => {
    if (!loaded) return;
    setLocalPreviewUrl(activeEffective.media_url || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeNetwork, overrideActive, loaded]);

  const contentColumn = (
    <div className="space-y-4">
      <div>
        <p className="text-xs font-medium text-[#64748B] mb-2">¿Dónde publicamos?</p>
        <div className="flex flex-wrap gap-2">
          {networks.map((n) => {
            const active = selectedNetworks.includes(n.key);
            const disabled = n.status !== 'active' || !editable;
            return (
              <button key={n.key} type="button" disabled={disabled} onClick={() => toggleNetwork(n.key)}
                data-testid={`composer-network-${n.key}`}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                  disabled ? 'border-[#E2E8F0] text-[#CBD5E1] cursor-not-allowed bg-[#F8FAFC]' :
                  active ? 'border-[#F72585] bg-[#F72585]/10 text-[#F72585]' : 'border-[#E2E8F0] text-[#64748B] hover:border-[#94A3B8]'}`}>
                {active && <Check className="w-3 h-3" />}
                {n.label}
                {n.status === 'active' && !n.connected && <span className="text-[10px] text-amber-600">(conectar)</span>}
                {n.status === 'active' && n.connected && n.key === 'facebook' && n.can_publish === false && <span className="text-[10px] text-amber-600">(sin permiso)</span>}
                {n.status === 'coming_soon' && <span className="text-[10px]">(próximamente)</span>}
              </button>
            );
          })}
        </div>
      </div>

      <Tabs value={contentTab} onValueChange={setContentTab}>
        <TabsList>
          <TabsTrigger value="editar" className="gap-1"><PenLine className="w-3.5 h-3.5" /> Contenido</TabsTrigger>
          <TabsTrigger value="ia" className="gap-1"><Sparkles className="w-3.5 h-3.5" /> Copy IA</TabsTrigger>
          <TabsTrigger value="hashtags" className="gap-1"><Hash className="w-3.5 h-3.5" /> Hashtags</TabsTrigger>
        </TabsList>

        <TabsContent value="editar" className="space-y-3 mt-3">
          {selectedNetworks.length > 1 && (
            <div className="flex items-center gap-2">
              {selectedNetworks.map((net) => (
                <button key={net} type="button" onClick={() => setActiveNetwork(net)}
                  className={`text-xs px-2 py-1 rounded-md border ${activeNetwork === net ? 'border-[#F72585] text-[#F72585]' : 'border-[#E2E8F0] text-[#64748B]'}`}>
                  {NETWORK_LABELS[net]}
                </button>
              ))}
              <div className="flex items-center gap-1.5 ml-auto">
                <span className="text-[11px] text-[#64748B]">Editar por red</span>
                <Switch checked={activeVariant.use_override} onCheckedChange={(v) => setVariantField(activeNetwork, 'use_override', v)} />
              </div>
            </div>
          )}

          <div>
            <div className="flex items-center gap-1.5 mb-1">
              <EmojiPicker onSelect={insertAtCursor} />
              <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={() => setUtmOpen(true)}><Link2 className="w-3.5 h-3.5" /> UTM</Button>
              <span className="text-[10px] text-[#94A3B8] ml-auto">{contentText.length} caracteres</span>
            </div>
            {activeVariant.use_override ? (
              <Textarea ref={contentRef} value={activeVariant.content_text} onChange={(e) => setVariantField(activeNetwork, 'content_text', e.target.value)}
                rows={5} placeholder={`Texto solo para ${NETWORK_LABELS[activeNetwork]}`} disabled={!editable} />
            ) : (
              <Textarea ref={contentRef} value={contentText} onChange={(e) => setContentText(e.target.value)} rows={5} placeholder="¿Qué quieres publicar?" disabled={!editable} data-testid="composer-text" />
            )}
          </div>

          <div>
            <p className="text-xs font-medium text-[#64748B] mb-1 flex items-center gap-1"><ImageIcon className="w-3.5 h-3.5" /> Imagen o video</p>
            {!localPreviewUrl ? (
              <div className="flex gap-2">
                <Input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,video/mp4,video/quicktime,video/webm"
                  onChange={(e) => onFileSelected(e.target.files?.[0])} disabled={!editable} data-testid="composer-file-input" />
                <Button type="button" variant="outline" size="sm" className="gap-1.5 whitespace-nowrap" onClick={() => setLibraryOpen(true)}><FolderOpen className="w-3.5 h-3.5" /> Biblioteca</Button>
              </div>
            ) : (
              <div className="space-y-2">
                {showCropper && !isVideo ? (
                  <div className="relative w-full h-56 bg-black rounded-lg overflow-hidden">
                    <Cropper image={localPreviewUrl} crop={crop} zoom={zoom} aspect={(postType === 'story' || postType === 'reel') ? 9 / 16 : 1}
                      onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={(_a, px) => setCroppedAreaPixels(px)} />
                  </div>
                ) : isVideo ? (
                  <video src={localPreviewUrl} controls className="w-full max-h-56 rounded-lg" />
                ) : (
                  <img src={localPreviewUrl} alt="" className="w-full max-h-56 object-contain rounded-lg border border-[#E2E8F0]" />
                )}
                <div className="flex items-center gap-2">
                  {showCropper && <Button size="sm" onClick={applyCrop} disabled={uploading} className="gap-1">{uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CropIcon className="w-3.5 h-3.5" />} Aplicar recorte</Button>}
                  {mediaUrl && <Button size="sm" variant="outline" asChild className="gap-1"><a href={mediaUrl} target="_blank" rel="noreferrer" download><Download className="w-3.5 h-3.5" /> Descargar</a></Button>}
                  <Button size="sm" variant="outline" onClick={removeMedia} disabled={!editable} className="gap-1"><X className="w-3.5 h-3.5" /> Quitar</Button>
                  {uploading && <Loader2 className="w-4 h-4 animate-spin text-[#94A3B8]" />}
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Tipo de publicación</Label>
              <Select value={postType} onValueChange={setPostType} disabled={!editable}>
                <SelectTrigger data-testid="composer-post-type"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(POST_TYPE_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Objetivo</Label>
              <Select value={objective} onValueChange={setObjective} disabled={!editable}>
                <SelectTrigger><SelectValue placeholder="Elegir" /></SelectTrigger>
                <SelectContent>{OBJECTIVES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">CTA</Label>
              <Input value={cta} onChange={(e) => setCta(e.target.value)} placeholder="Comprar ahora" disabled={!editable} />
            </div>
            <div>
              <Label className="text-xs">URL del CTA</Label>
              <Input value={ctaUrl} onChange={(e) => setCtaUrl(e.target.value)} placeholder="https://..." disabled={!editable} />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Audiencia</Label>
              <Input value={audience} onChange={(e) => setAudience(e.target.value)} disabled={!editable} />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Notas internas (nunca se publican)</Label>
              <Textarea value={internalNotes} onChange={(e) => setInternalNotes(e.target.value)} rows={2} disabled={!editable} />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-[#94A3B8]" />
            <Input type="datetime-local" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} className="w-56" disabled={!editable} data-testid="composer-schedule" />
          </div>
        </TabsContent>

        <TabsContent value="ia" className="mt-3">
          <AiCopyStudio network={selectedNetworks.length === 1 ? selectedNetworks[0] : 'both'} onApply={applyAiCopy} />
        </TabsContent>

        <TabsContent value="hashtags" className="mt-3">
          <HashtagAssistant topic={contentText.slice(0, 100)} network={activeNetwork} selected={hashtags} onChange={setHashtags} />
        </TabsContent>
      </Tabs>

      {(validation.errors.length > 0 || validation.warnings.length > 0) && (
        <div className="space-y-1.5 border border-[#E2E8F0] rounded-lg p-2.5">
          {validation.errors.map((e, i) => (
            <div key={`e${i}`} className="flex items-start gap-1.5 text-xs text-red-600"><AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" /> {e}</div>
          ))}
          {validation.warnings.map((w, i) => (
            <div key={`w${i}`} className="flex items-start gap-1.5 text-xs text-amber-600"><AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" /> {w}</div>
          ))}
        </div>
      )}
    </div>
  );

  const previewColumn = (
    <div className="space-y-3">
      {selectedNetworks.length > 1 && (
        <div className="flex gap-2">
          {selectedNetworks.map((net) => (
            <button key={net} type="button" onClick={() => setActiveNetwork(net)}
              className={`text-xs px-2 py-1 rounded-md border ${activeNetwork === net ? 'border-[#F72585] text-[#F72585]' : 'border-[#E2E8F0] text-[#64748B]'}`}>
              {NETWORK_LABELS[net]}
            </button>
          ))}
        </div>
      )}
      <p className="text-[10px] text-[#94A3B8]">Vista previa aproximada — el resultado real puede variar según el dispositivo.</p>
      <div className="border border-[#E2E8F0] rounded-xl p-3 bg-white max-w-sm mx-auto">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-7 h-7 rounded-full bg-[#F1F5F9]" />
          <div>
            <p className="text-xs font-semibold text-[#0F172A]">Tu {NETWORK_LABELS[activeNetwork] || 'cuenta'}</p>
            <p className="text-[10px] text-[#94A3B8]">{POST_TYPE_LABELS[activeEffective.post_type] || activeEffective.post_type} · {scheduledAt ? new Date(scheduledAt).toLocaleString('es-MX') : 'Ahora'}</p>
          </div>
        </div>
        {activeEffective.media_url && (
          activeEffective.is_video ? <video src={activeEffective.media_url} controls className="w-full rounded-lg mb-2 max-h-64" /> : <img src={activeEffective.media_url} alt="" className="w-full rounded-lg mb-2 max-h-64 object-cover" />
        )}
        <p className="text-xs text-[#0F172A] whitespace-pre-wrap">{activeEffective.content_text || '(sin texto)'}</p>
        {activeEffective.hashtags?.length > 0 && <p className="text-[11px] text-blue-600 mt-1">{activeEffective.hashtags.map((h) => `#${h}`).join(' ')}</p>}
        {cta && <p className="text-[11px] text-[#F72585] font-medium mt-2">{cta} →</p>}
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <DialogTitle>{post ? 'Editar publicación' : 'Nueva publicación'}</DialogTitle>
            <span className="text-[11px] text-[#94A3B8] flex items-center gap-1">
              {autosaveState === 'saving' && <><Loader2 className="w-3 h-3 animate-spin" /> Guardando...</>}
              {autosaveState === 'saved' && 'Guardado'}
              {autosaveState === 'error' && <span className="text-red-500">Error al guardar</span>}
            </span>
          </div>
        </DialogHeader>

        {!loaded ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#94A3B8]" /></div>
        ) : (
          <div className="hidden lg:grid lg:grid-cols-2 gap-6">
            {contentColumn}
            {previewColumn}
          </div>
        )}
        {loaded && (
          <div className="lg:hidden">
            <Tabs value={mobileTab} onValueChange={setMobileTab}>
              <TabsList className="w-full">
                <TabsTrigger value="content" className="flex-1">Contenido</TabsTrigger>
                <TabsTrigger value="preview" className="flex-1">Vista previa</TabsTrigger>
              </TabsList>
              <TabsContent value="content">{contentColumn}</TabsContent>
              <TabsContent value="preview">{previewColumn}</TabsContent>
            </Tabs>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>Cerrar</Button>
          <Button onClick={submit} disabled={submitting || uploading || showCropper || validation.errors.length > 0} className="gap-2" data-testid="composer-submit">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {scheduledAt ? 'Programar' : 'Publicar ahora'}
          </Button>
        </DialogFooter>
      </DialogContent>

      <UtmBuilder open={utmOpen} onClose={() => setUtmOpen(false)} network={activeNetwork} onInsert={insertAtCursor} />
      <MediaLibraryPicker open={libraryOpen} onClose={() => setLibraryOpen(false)} onSelect={pickFromLibrary} />
    </Dialog>
  );
}
